import React from 'react';
import { StyleSheet, View } from 'react-native';
import { useScrollspy } from '@sg/garnish';
import {
  type DietaryProperty,
  type IngredientModification,
} from '@sg/graphql-schema';
import {
  CustomizationIngredientsGrid,
  useCustomizationStepperTimeout,
} from '@sg/shared-fe/features/customization';

import {
  checkIfMaxModificationLimitExceeded,
  type ProductIngredientModifications,
} from '../../../../state';
import {
  type FilteredIngredientModifications,
  type IngredientModificationWithQuantity,
} from '../../../../types';
import { OutOfStockModal } from '../../../OutOfStockModal';
import {
  getFormattedQuantityByKind,
  getSelectedQuantityByKind,
  useSectionHelperText,
} from '../../helpers';
import { ProductDetailsIngredientCardWrapper } from '../ProductDetailsIngredientCardWrapper';

// ─────────────────────────────────────────────────────────────────────────────

export const IngredientsGridPanel = (props: IngredientsGridPanelProps) => {
  const {
    modifications,
    ingredientsModificationsKinds,
    ingredientsModifications,
    activeIngredientsModificationsMap,
    defaultActiveTargetId,
    addIngredientModification,
    removeIngredientModification,
    getRestrictionNotice,
  } = props;

  const getSectionHelperText = useSectionHelperText();

  const { active, limitations, maxModifications } = modifications;

  const hasMaxModifications = checkIfMaxModificationLimitExceeded({
    ...modifications,
    maxModifications:
      maxModifications > 0 ? maxModifications - 1 : maxModifications,
  });

  const { activeStepperId, setActiveStepperId, resetStepperTimeout } =
    useCustomizationStepperTimeout();

  const { helpers, setActiveTargetId } = useScrollspy({
    defaultActiveTargetId,
  });

  // ─────────────────────────────────────────────────────────────────────

  if (ingredientsModificationsKinds.length === 0) return null;

  return (
    <View style={styles.ingredientsGridContainer}>
      <CustomizationIngredientsGrid.Container
        register={helpers.scrollView.register}
        deregister={helpers.scrollView.deregister}
        storeContentSize={helpers.scrollView.storeContentSize}
        storeSize={helpers.scrollView.storeSize}
        trackScroll={helpers.scrollView.trackScroll}
      >
        {Object.entries(ingredientsModifications).map(
          ([ingredientKindName, ingredientKind]) => {
            const selectedQuantity = getSelectedQuantityByKind({
              active,
              ingredientKindName,
            });

            const formattedQuantity = getFormattedQuantityByKind({
              active,
              ingredientsModifications,
              ingredientKindName,
            });

            const maxQuantity =
              limitations?.[ingredientKindName]?.max ??
              Number.POSITIVE_INFINITY;

            const isAtMaxQuantity =
              selectedQuantity >= maxQuantity || hasMaxModifications;

            const helperText = getSectionHelperText(
              ingredientsModifications,
              ingredientKindName,
            );

            return (
              <CustomizationIngredientsGrid.Section
                key={ingredientKindName}
                sectionId={ingredientKindName}
                ingredientKindName={ingredientKindName}
                formattedQuantity={formattedQuantity}
                helperText={helperText}
                register={helpers.target.register}
                deregister={helpers.target.deregister}
              >
                <ProductDetailsIngredientCardWrapper
                  palette="spinach"
                  ingredientModifications={ingredientKind.modifications}
                  activeIngredientsModificationsMap={activeIngredientsModificationsMap} // prettier-ignore
                  isAtMaxQuantity={isAtMaxQuantity}
                  activeStepperId={activeStepperId}
                  setActiveStepperId={setActiveStepperId}
                  onIncrement={addIngredientModification}
                  onDecrement={removeIngredientModification}
                  getRestrictionNotice={getRestrictionNotice}
                  resetStepperTimeout={resetStepperTimeout}
                />
              </CustomizationIngredientsGrid.Section>
            );
          },
        )}
      </CustomizationIngredientsGrid.Container>

      <OutOfStockModal setIngredientCategory={setActiveTargetId} />
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  ingredientsGridContainer: {
    flex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type IngredientsGridPanelProps = {
  modifications: ProductIngredientModifications;
  defaultActiveTargetId: string;
  ingredientsModifications: Partial<FilteredIngredientModifications>;
  ingredientsModificationsKinds: string[];
  activeIngredientsModificationsMap: Map<
    string,
    IngredientModificationWithQuantity
  >;
  addIngredientModification: (
    ingredientModification: IngredientModification,
  ) => void;
  removeIngredientModification: (
    ingredientModification: IngredientModification,
  ) => void;
  getRestrictionNotice: (
    dietaryProperties: readonly DietaryProperty[] | undefined,
  ) => string;
};
